import React, { useState, useEffect } from "react";
import SlideOverlay from "./SlideOverlay";
import OrbitalSystem from "../OrbitalSystem";
import ParticleFusion from "../ParticleFusion";

/* ────────────────────────────────────────────────
 *  Pre-compiled noise keyframes for clip-path aberration
 *  30 steps each with seeded pseudo-random insets
 * ──────────────────────────────────────────────── */
function generateNoiseKeyframes(name, seed) {
  let s = seed;
  const rand = (max) => {
    s = (s * 16807 + 11) % 2147483647;
    return Math.abs(s) % max;
  };

  const steps = 30;
  let css = `@keyframes ${name} {\n`;
  for (let i = 0; i <= steps; i++) {
    const pct = ((i / steps) * 100).toFixed(2);
    const top = rand(100);
    const bottom = rand(101 - top);
    css += `  ${pct}% { clip-path: inset(${top}% 0 ${bottom}% 0); }\n`;
  }
  css += `}`;
  return css;
}

const IMAGE_GLITCH_STYLES = `
  /* ── Step-end cyber glitch (delayed after vortex) ── */
  @keyframes imageCyberGlitch {
    0%   { transform: translate(0) scale(1); filter: none; opacity: 1; }
    5%   { transform: translate(-30px, 15px) scale(1.1) skewX(35deg); filter: drop-shadow(-15px 0 0 #ff003c) drop-shadow(15px 0 0 #00f0ff) brightness(2); opacity: 0.8; }
    10%  { transform: translate(30px, -15px) scale(0.9) skewX(-35deg); filter: drop-shadow(15px 0 0 #ff003c) drop-shadow(-15px 0 0 #00f0ff) invert(0.8) contrast(2); opacity: 0.9; }
    15%  { transform: translate(0) scale(1); filter: none; opacity: 1; }
    25%  { transform: translate(0) scale(1); filter: none; opacity: 1; }
    30%  { transform: translate(-15px, -5px) scale(1.05) skewX(15deg); filter: drop-shadow(-20px 5px 0 #fcee0a) drop-shadow(20px -5px 0 #00f0ff) brightness(3); opacity: 0.6; }
    35%  { transform: translate(0) scale(1); filter: none; opacity: 1; }
    60%  { transform: translate(0) scale(1); filter: none; opacity: 1; }
    65%  { transform: translate(20px, 10px) scale(1) skewX(-20deg); filter: drop-shadow(25px 0 0 #ff003c) drop-shadow(-25px 0 0 #00f0ff) hue-rotate(90deg); opacity: 0.7; }
    70%  { transform: translate(0) scale(1); filter: none; opacity: 1; }
    85%  { transform: translate(-5px, 5px) scale(1.02) skewX(5deg); filter: drop-shadow(-10px 0 0 #ff003c) drop-shadow(10px 0 0 #00f0ff); opacity: 0.9; }
    90%  { transform: translate(0) scale(1); filter: none; opacity: 1; }
    100% { transform: translate(0) scale(1); filter: none; opacity: 1; }
  }

  /* ── Glitch image container ── */
  .glitch-img {
    position: absolute;
    inset: 0;
    background-size: cover;
    background-position: center;
    pointer-events: none;
    animation: imageCyberGlitch 0.45s 0.5s step-end forwards;
    will-change: transform, filter, opacity;
    contain: layout style paint;
  }

  /* Chromatic aberration layers */
  .glitch-img::before,
  .glitch-img::after {
    content: "";
    position: absolute;
    inset: 0;
    background: inherit;
    background-size: cover;
    background-position: center;
    pointer-events: none;
  }

  /* Magenta shift — left offset */
  .glitch-img::before {
    left: -2px;
    mix-blend-mode: screen;
    filter: hue-rotate(290deg) saturate(2) brightness(1.1);
    opacity: 0.6;
    animation: imgNoise1 3s linear infinite alternate-reverse;
  }

  /* Green shift — right offset */
  .glitch-img::after {
    left: 2px;
    mix-blend-mode: screen;
    filter: hue-rotate(90deg) saturate(2) brightness(1.1);
    opacity: 0.6;
    animation: imgNoise2 3s linear infinite alternate-reverse;
  }

  /* ── Glow layer ── */
  .glitch-glow {
    position: absolute;
    inset: 0;
    background-size: cover;
    background-position: center;
    pointer-events: none;
    filter: blur(40px) brightness(0.4);
    opacity: 0.5;
    will-change: opacity;
    contain: layout style paint;
  }

  ${generateNoiseKeyframes("imgNoise1", 42)}
  ${generateNoiseKeyframes("imgNoise2", 137)}
`;

export default function SlideAdaptive() {
  const imgUrl = "./carousel_img/adaptive.webp";
  const [effectActive, setEffectActive] = useState(true);
  const [vortexGlitchKey, setVortexGlitchKey] = useState(0);

  // After 2s: strip the image effect, re-trigger vortex glitch
  useEffect(() => {
    const timer = setTimeout(() => {
      setEffectActive(false);
      setVortexGlitchKey((k) => k + 1);
    }, 2000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      <style>{IMAGE_GLITCH_STYLES}</style>

      {/* 1. Background Color Layer */}
      <div className="absolute inset-0 bg-[#1e1e20]" />

      {/* 2. Middle Layer: Orbital System (Particles) */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <OrbitalSystem glitchKey={vortexGlitchKey} />
      </div>

      {/* 3. Foreground Image Layer — Cyber Glitch Effect (stripped at 2s) */}
      <div
        className={
          effectActive
            ? "glitch-img"
            : "absolute inset-0 bg-cover bg-center pointer-events-none"
        }
        style={{ backgroundImage: `url(${imgUrl})` }}
      >
        <img
          src={imgUrl}
          alt=""
          className="hidden"
          loading="lazy"
          decoding="async"
        />
      </div>

      {/* 3b. Glow layer (removed with effect) */}
      {effectActive && (
        <div
          className="glitch-glow"
          style={{ backgroundImage: `url(${imgUrl})` }}
        />
      )}

      {/* 4. Top Layer: Particle Fusion Effect */}
      <div className="absolute inset-0 z-10 pointer-events-none overflow-hidden isolate">
        <div
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
          style={{
            width: "max(100vw, calc(100vh * 16 / 9))",
            height: "max(100vh, calc(100vw * 9 / 16))",
          }}
        >
          <ParticleFusion top="67.5%" left="50.7%" />
        </div>
      </div>

      {/* 5. Text Overlay */}
      <SlideOverlay
        label="Reduce Noise & Interferences"
        title="Adaptive Insights"
        description="Bridge the gap between human creativity and machine precision. Our intuitive interfaces translate complex neural data into actionable, real-time insights for your team"
        ctaText="Discover Predictive Analytics"
        ctaUrl="/solutions/predictive-analytics"
      />
    </>
  );
}
